/*
 * portl-click-to-play.js
 * ──────────────────────────────────────────────────────────────────
 * Shared utility — click-to-play inline video lazy loader.
 *
 * Pattern: show a poster image with a play icon over a <video>
 * element that has NO src and NO autoplay. Only when the user
 * clicks does the script set the src, load(), and play(). On end
 * (or external "reset" trigger), removes src + load() to release
 * the video decoder, returning the element to its poster state.
 *
 * USAGE (in any section's markup):
 *
 *   <div class="my-section__media" data-portl-click-to-play>
 *     <video
 *       class="my-section__video"
 *       playsinline webkit-playsinline
 *       preload="none"
 *       data-src-desktop="hero-desktop.mp4"
 *       data-src-mobile="hero-mobile.mp4"
 *       data-poster-desktop="poster-desktop.webp"
 *       data-poster-mobile="poster-mobile.webp"
 *     ></video>
 *     <button type="button"
 *             class="my-section__play"
 *             data-portl-click-to-play-button
 *             aria-label="Play video">
 *       <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
 *         <polygon points="5 3 19 12 5 21 5 3"/>
 *       </svg>
 *     </button>
 *     <img class="my-section__poster"
 *          data-portl-click-to-play-poster
 *          src="poster-desktop.webp"
 *          alt=""
 *          loading="lazy">
 *   </div>
 *
 * Then include the script ONCE per page (in theme.liquid):
 *
 *   <script src="{{ 'portl-click-to-play.js' | asset_url }}" defer></script>
 *
 * BEHAVIOR:
 *   • Initial state: poster + play button visible, video has no src
 *     → ZERO bytes fetched for the video file itself
 *   • Click anywhere on the container (or the play button):
 *       1. Resolve the appropriate src for current viewport
 *       2. Set <video src=...>
 *       3. video.load() then video.play() with iOS canplay handoff
 *       4. Hide poster + play button (CSS via `.is-playing` class)
 *   • Video ended (or user clicks .reset trigger):
 *       1. Pause
 *       2. removeAttribute('src')
 *       3. load() — releases the decoder + memory
 *       4. Show poster + play button again
 *
 * iOS NOTES:
 *   • Uses canplay/loadeddata event handoff so play() doesn't fire
 *     against an empty video. Required for iOS Safari + iOS Chrome
 *     (both WebKit) where play() rejects on readyState=0.
 *   • Sources are set EAGER on click — Shopify CDN doesn't return
 *     CORS, so no crossorigin attribute is added.
 *   • playsinline + webkit-playsinline are REQUIRED on the markup
 *     (this script doesn't add them — would be too late on iOS).
 *
 * MARKUP CONTRACT:
 *   Container:      [data-portl-click-to-play]
 *   Video:          <video> (any class, must be a child)
 *   Play button:    [data-portl-click-to-play-button] (optional —
 *                   if absent, the container itself is the trigger)
 *   Poster image:   [data-portl-click-to-play-poster] (optional)
 *   Reset trigger:  [data-portl-click-to-play-reset] (optional —
 *                   click returns to poster state without ending
 *                   the video naturally)
 *
 *   Sources:
 *     <video data-src="..."> (single source, all viewports), OR
 *     <video data-src-mobile="..." data-src-desktop="...">
 *       (responsive — script picks based on window.innerWidth)
 *
 * CSS HOOKS (define in your section's stylesheet):
 *   [data-portl-click-to-play] { position: relative; }
 *   [data-portl-click-to-play] video { ... }
 *   [data-portl-click-to-play] [data-portl-click-to-play-button] {
 *     position: absolute; inset: 50% auto auto 50%;
 *     transform: translate(-50%, -50%);
 *     transition: opacity 0.2s ease;
 *   }
 *   [data-portl-click-to-play].is-playing
 *     [data-portl-click-to-play-button],
 *   [data-portl-click-to-play].is-playing
 *     [data-portl-click-to-play-poster] {
 *     opacity: 0;
 *     pointer-events: none;
 *   }
 * ──────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) return;

  /* Responsive source picker. Mobile breakpoint matches the rest of
     the theme (1024px). Returns the URL the <video> should fetch. */
  function pickSrc(video) {
    var isMobile = window.innerWidth < 1024;
    if (isMobile) {
      return video.getAttribute('data-src-mobile')
          || video.getAttribute('data-src')
          || video.getAttribute('data-src-desktop')
          || '';
    }
    return video.getAttribute('data-src-desktop')
        || video.getAttribute('data-src')
        || video.getAttribute('data-src-mobile')
        || '';
  }

  function startPlayback(container, video) {
    var url = pickSrc(video);
    if (!url) {
      try { console.warn('[portl-click-to-play] no src configured', container); } catch (e) {}
      return;
    }

    /* If src is already set to the right URL we just need to play.
       This handles the case where the user paused via native
       controls then clicks the play button again. */
    if (video.getAttribute('src') !== url) {
      video.setAttribute('src', url);
      try { video.load(); } catch (e) {}
    }

    container.classList.add('is-playing');
    /* Surface native controls so the user can pause / scrub / mute /
       fullscreen once playback starts. Removed on reset so the
       initial state stays clean (poster + play button only). */
    video.setAttribute('controls', '');

    function attemptPlay() {
      /* iOS Safari guarantees muted play() succeeds inside a user
         gesture. We start unmuted (the click IS a user gesture)
         but fall back to muted if the unmuted attempt rejects. */
      video.muted = false;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          video.muted = true;
          try { video.play().catch(function () {}); } catch (e) {}
        });
      }
    }

    /* readyState: 0=HAVE_NOTHING, 1=HAVE_METADATA, 2=HAVE_CURRENT_DATA.
       iOS rejects play() on a HAVE_NOTHING video even inside a click
       handler. Wait for canplay/loadeddata before calling play(). */
    if (video.readyState >= 2) {
      attemptPlay();
    } else {
      var onReady = function () {
        video.removeEventListener('canplay', onReady);
        video.removeEventListener('loadeddata', onReady);
        attemptPlay();
      };
      video.addEventListener('canplay', onReady);
      video.addEventListener('loadeddata', onReady);
      /* Also try right now — some browsers tolerate this and start
         decoding once bytes arrive without needing the handoff. */
      attemptPlay();
    }
  }

  function resetToPoster(container, video) {
    try { video.pause(); } catch (e) {}
    /* Spec-compliant decoder release sequence: remove src then call
       load(). The empty load() tells the browser to abandon any
       in-flight network request and free decoder/buffer resources. */
    try {
      video.removeAttribute('src');
      /* Also clear any <source> children if present (defensive — most
         click-to-play videos use the data-src attribute on the
         <video> element directly, not <source> children). */
      var sources = video.querySelectorAll('source');
      Array.prototype.forEach.call(sources, function (s) { s.removeAttribute('src'); });
      video.load();
    } catch (e) {}
    container.classList.remove('is-playing');
    /* Drop native controls so the next interaction starts from the
       clean poster state. */
    video.removeAttribute('controls');
  }

  function bindContainer(container) {
    if (container.dataset.portlClickToPlayBound === '1') return;
    var video = container.querySelector('video');
    if (!video) return;
    container.dataset.portlClickToPlayBound = '1';

    var trigger = container.querySelector('[data-portl-click-to-play-button]') || container;
    var resetBtn = container.querySelector('[data-portl-click-to-play-reset]');

    trigger.addEventListener('click', function (e) {
      /* If already playing, the click is on the video chrome —
         don't re-trigger. The native controls handle interaction. */
      if (container.classList.contains('is-playing')) return;
      e.preventDefault();
      startPlayback(container, video);
    });

    video.addEventListener('ended', function () {
      /* When the video ends naturally, return to poster state and
         free the decoder. Skipped if `loop` attribute is set. */
      if (video.hasAttribute('loop')) return;
      resetToPoster(container, video);
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        resetToPoster(container, video);
      });
    }
  }

  function scan(root) {
    root = root || document;
    var nodes = root.querySelectorAll('[data-portl-click-to-play]');
    Array.prototype.forEach.call(nodes, bindContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) scan(e.target);
  });

  /* Public API for sections that dynamically inject click-to-play
     containers (e.g. an admin block that's added without a section
     reload). */
  window.PortlClickToPlay = {
    bind: bindContainer,
    scan: scan,
    reset: resetToPoster
  };
})();
